import { Events } from 'core/events/Events';
import { DOMHelper } from 'core/DOMHelper';
import { Paging } from 'app/module/global/Paging';
import { QueProjectEvent } from 'app/events/QueProjectEvent';
import { Simple } from 'utils/analytics';
import { WorkPagingDeps } from './work.types';

/**
 * Paging side of the work section: the Paging module interop, the focused
 * index / focused UI theme state, the paging guide effect and auto-paging
 * driven from the drag progress.
 */
export class WorkPaging {
	private _dom: DOMHelper;
	private _deps: WorkPagingDeps;

	private _paging: Paging;
	private _pagingGuideActive: boolean = false;
	private _pagingGuideStartProgress: number = 0;
	private _focusedIndex: number = 0;
	private _focusedUITheme: string;

	private _disablePagingAutoUpdate: boolean = false;
	private _autoPagingCounter: number = 0;

	constructor(dom: DOMHelper, deps: WorkPagingDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	//Accessors
	//-------------------------------------------------------------------------------------

	public get isCreated(): boolean {
		return !!this._paging;
	}

	public get focusedIndex(): number {
		return this._focusedIndex;
	}

	public set focusedIndex(index: number) {
		this._focusedIndex = index;
	}

	public get focusedUITheme(): string {
		return this._focusedUITheme;
	}

	public set guideStartProgress(progress: number) {
		this._pagingGuideStartProgress = progress;
	}

	public set disableAutoUpdate(value: boolean) {
		this._disablePagingAutoUpdate = value;
	}

	public get disableAutoUpdate(): boolean {
		return this._disablePagingAutoUpdate;
	}

	public set active(value: boolean) {
		this._paging.active = value;
	}

	//Paging module delegation
	//-------------------------------------------------------------------------------------

	/** Setup paging */
	public setup(container: Element, department: string): void {
		this._paging = new Paging();
		this._paging.setContainer(container.querySelector(`#${department}-paging`));
		this._paging.id = `${department}-paging`;
		this._paging.create();
		this._paging.setCallbacks((index: number) => this._deps.onGotoProject(index));
	}

	public reset(): void {
		this._paging.reset();
	}

	public setActiveIndex(index: number): void {
		this._paging.setActiveIndex(index);
	}

	public getActiveIndex(): number {
		return this._paging.getActiveIndex();
	}

	public hide(): void {
		this._paging.hide();
	}

	public show(): void {
		this._paging.show();
	}

	//Guide effect
	//-------------------------------------------------------------------------------------

	/** Show homepage guid */
	public showGuide(animate: boolean = true): void {
		this._pagingGuideActive = true;
		this._paging.startGuideEffect(animate);
	}

	/** Check Paging is passed */
	public checkGuidePassed(tileProgress: number): void {
		if (!this._pagingGuideActive) {
			return;
		}

		if (tileProgress > 0 && tileProgress !== this._pagingGuideStartProgress) {
			this._pagingGuideActive = false;
			this._paging.removeGuideEffect();
		}
	}

	//Auto paging / focus
	//-------------------------------------------------------------------------------------

	/** Set auto paging. Don't check every frame as not animation critical */
	public setAutoPaging(tileProgress: number): void {
		this._autoPagingCounter--;

		if (!this._disablePagingAutoUpdate && this._autoPagingCounter <= 0) {
			let oldIndex: number = this._paging.getActiveIndex();
			let newIndex: number = this._paging.checkIndexFromRatio(tileProgress % 1);
			this._autoPagingCounter = 2;

			if (oldIndex !== newIndex) {
				this.focusIndexChanged(newIndex);
			}
		}
	}

	/** Focused index has changed. */
	public focusIndexChanged(index: number): void {
		//The user is over a project in the line, so que the video (or image) for preloading
		//If project HTML section has not been injected, it will at this point
		this._focusedIndex = index;
		this._focusedUITheme =
			this._deps.getProject(index - 1) && this._deps.getProject(index - 1).topUiTheme
				? this._deps.getProject(index - 1).topUiTheme
				: 'light';

		this._deps.queueIntroPrompt();

		if (index != 0 && index) {
			this._dom.addClass(document.querySelector(`.work #${this._deps.getDepartment()}-paging`), '-show');

			Events.dispatch(
				new QueProjectEvent({
					slug: this._paging.getActiveSlug(this._focusedIndex),
					index: index,
					headerType: this._deps.getProject(index - 1) ? this._deps.getProject(index - 1).headerType : null,
					topUITheme: this._focusedUITheme
				})
			);

			Simple.event('work_project_snapped', {
				department: this._deps.getDepartment(),
				slug: this._deps.getProject(index - 1).slug
			});
		} else {
			this._dom.removeClass(document.querySelector(`.work #${this._deps.getDepartment()}-paging`), '-show');

			Simple.event('work_back_to_intro', { department: this._deps.getDepartment() });
		}
	}
}
